import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api } from '../services/api';
import MobileHeader from '../components/MobileHeader';
import Notification from '../components/Notification';
import ListSelectionModal from '../components/ListSelectionModal';
import RecurringTaskModal from '../components/RecurringTaskModal';
import ConfirmModal from '../components/ConfirmModal';
import MentionAutocomplete from '../components/MentionAutocomplete';
import UserAvatar from '../components/UserAvatar';
import { linkifyText } from '../utils/textUtils';
import { isMainList, isTomorrowList } from '../constants/listTypes';
import { isAfterPromotionCutoff } from '../utils/timeUtils';
import './TaskDetail.css';

export default function TaskDetail({ lists, setLists, tomorrowCutoffHour = 18, onTaskDataChange, onDeleteTask }) {
  const { taskId } = useParams();
  const navigate = useNavigate();
  const [task, setTask] = useState(null);
  const [listId, setListId] = useState(null);
  const [taskText, setTaskText] = useState('');
  const [assignedUserId, setAssignedUserId] = useState('');
  const [isAssigneeMenuOpen, setIsAssigneeMenuOpen] = useState(false);
  const [subTasks, setSubTasks] = useState([]);
  const [newSubTaskText, setNewSubTaskText] = useState('');
  const [notes, setNotes] = useState([]);
  const [newNoteText, setNewNoteText] = useState('');
  const [isSavingTask, setIsSavingTask] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [loading, setLoading] = useState(true);
  const [isAiEnhancing, setIsAiEnhancing] = useState(false);
  const [notification, setNotification] = useState({ isVisible: false, message: '', type: 'success' });
  const [showMoveModal, setShowMoveModal] = useState(false);
  const [showRecurringModal, setShowRecurringModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isMoving, setIsMoving] = useState(false);
  const taskInputRef = useRef(null);
  const subTaskInputRef = useRef(null);
  const noteInputRef = useRef(null);
  const assigneeRef = useRef(null);

  // Find task from lists
  useEffect(() => {
    if (lists.length > 0 && taskId) {
      let found = false;
      for (const list of lists) {
        const foundTask = list.tasks.find(t => t.id === taskId);
        if (foundTask) {
          setTask(foundTask);
          setListId(list.id);
          setTaskText(foundTask.text);
          setLoading(false);
          found = true;
          break;
        }
      }
      if (!found) {
        setLoading(false);
      }
    }
  }, [lists, taskId]);

  // Load sub-tasks and notes from API
  useEffect(() => {
    if (taskId) {
      const loadTaskData = async () => {
        try {
          const [subTasksData, notesData] = await Promise.all([
            api.getSubTasks(taskId),
            api.getNotes(taskId)
          ]);
          
          setSubTasks(subTasksData || []);
          setNotes(notesData || []);
        } catch (error) {
          console.error('Error loading task data:', error);
          setSubTasks([]);
          setNotes([]);
        }
      };
      
      loadTaskData();
    }
  }, [taskId]);

  // Update task text when task prop changes (but don't reload sub-tasks/notes)
  useEffect(() => {
    if (task && task.text !== taskText && !hasUnsavedChanges) {
      setTaskText(task.text);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [task?.text, hasUnsavedChanges]);

  // Keep the assignee selection in sync with the task's assignee. Our own optimistic edits
  // update local state directly, so they won't fight this effect.
  useEffect(() => {
    setAssignedUserId(task?.assignedTo?.userId || '');
    setIsAssigneeMenuOpen(false);
  }, [task?.id, task?.assignedTo?.userId]);

  // Close the assignee dropdown when tapping outside of it
  useEffect(() => {
    if (!isAssigneeMenuOpen) return;
    const handleClickOutside = (e) => {
      if (assigneeRef.current && !assigneeRef.current.contains(e.target)) {
        setIsAssigneeMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isAssigneeMenuOpen]);

  // Focus task input when page loads
  useEffect(() => {
    if (!loading && task && taskInputRef.current) {
      setTimeout(() => {
        taskInputRef.current?.focus();
      }, 100);
    }
  }, [loading, task]);

  const handleBack = () => {
    navigate(-1);
  };

  const handleTaskTextSave = async () => {
    if (!task) return;
    const trimmed = taskText.trim();
    if (trimmed && trimmed !== task.text) {
      setIsSavingTask(true);
      try {
        await api.updateTask(task.id, trimmed);
        // Refresh lists
        const data = await api.getLists();
        setLists(data);
        // Update local task
        const updatedTask = data
          .flatMap(list => list.tasks)
          .find(t => t.id === task.id);
        if (updatedTask) {
          setTask(updatedTask);
        }
        setHasUnsavedChanges(false);
      } catch (error) {
        console.error('Error updating task:', error);
        setTaskText(task.text);
      } finally {
        setIsSavingTask(false);
      }
    } else if (!trimmed) {
      setTaskText(task.text);
      setHasUnsavedChanges(false);
    }
  };

  const handleTaskTextChange = (e) => {
    if (!task) return;
    setTaskText(e.target.value);
    setHasUnsavedChanges(e.target.value.trim() !== task.text);
  };

  const handleTaskTextKeyDown = (e) => {
    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      handleTaskTextSave();
      if (taskInputRef.current) {
        taskInputRef.current.blur();
      }
    }
  };

  const handleToggleTask = async () => {
    if (!task || !listId) return;
    try {
      await api.toggleTask(task.id);
      const data = await api.getLists();
      setLists(data);
      const updatedTask = data
        .flatMap(list => list.tasks)
        .find(t => t.id === task.id);
      if (updatedTask) {
        setTask(updatedTask);
      }
    } catch (error) {
      console.error('Error toggling task:', error);
    }
  };

  const handleAddSubTask = async () => {
    if (!task) return;
    const trimmed = newSubTaskText.trim();
    if (trimmed) {
      try {
        const newSubTask = await api.createSubTask(task.id, trimmed);
        setSubTasks([...subTasks, newSubTask]);
        setNewSubTaskText('');
        // Notify parent to refresh task data
        if (onTaskDataChange) {
          onTaskDataChange(task.id);
        }
        // Refresh lists
        const data = await api.getLists();
        setLists(data);
        setTimeout(() => {
          if (subTaskInputRef.current) {
            subTaskInputRef.current.focus();
          }
        }, 0);
      } catch (error) {
        console.error('Error creating sub-task:', error);
        alert('Failed to create sub-task. Please try again.');
      }
    }
  };

  const handleSubTaskKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddSubTask();
    }
  };

  const handleToggleSubTask = async (subTaskId) => {
    const subTask = subTasks.find(st => st.id === subTaskId);
    if (!subTask) return;
    
    const newCompleted = !subTask.completed;
    
    setSubTasks(subTasks.map(st => 
      st.id === subTaskId ? { ...st, completed: newCompleted } : st
    ));
    
    try {
      await api.updateSubTask(subTaskId, { completed: newCompleted });
      if (onTaskDataChange) {
        onTaskDataChange(task.id);
      }
      const data = await api.getLists();
      setLists(data);
    } catch (error) {
      console.error('Error updating sub-task:', error);
      setSubTasks(subTasks.map(st => 
        st.id === subTaskId ? { ...st, completed: subTask.completed } : st
      ));
      alert('Failed to update sub-task. Please try again.');
    }
  };

  const handleDeleteSubTask = async (subTaskId) => {
    const originalSubTasks = subTasks;
    setSubTasks(subTasks.filter(st => st.id !== subTaskId));
    
    try {
      await api.deleteSubTask(subTaskId);
      if (onTaskDataChange) {
        onTaskDataChange(task.id);
      }
      const data = await api.getLists();
      setLists(data);
    } catch (error) {
      console.error('Error deleting sub-task:', error);
      setSubTasks(originalSubTasks);
      alert('Failed to delete sub-task. Please try again.');
    }
  };

  const handleAddNote = async () => {
    if (!task) return;
    const trimmed = newNoteText.trim();
    if (trimmed) {
      try {
        const newNote = await api.createNote(task.id, trimmed);
        setNotes([...notes, newNote]);
        setNewNoteText('');
        if (onTaskDataChange) {
          onTaskDataChange(task.id);
        }
        const data = await api.getLists();
        setLists(data);
        setTimeout(() => {
          if (noteInputRef.current) {
            noteInputRef.current.focus();
          }
        }, 0);
      } catch (error) {
        console.error('Error creating note:', error);
        alert('Failed to create note. Please try again.');
      }
    }
  };

  const handleNoteKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleAddNote();
    }
  };

  const handleDeleteNote = async (noteId) => {
    const originalNotes = notes;
    setNotes(notes.filter(note => note.id !== noteId));
    
    try {
      await api.deleteNote(noteId);
      if (onTaskDataChange) {
        onTaskDataChange(task.id);
      }
      const data = await api.getLists();
      setLists(data);
    } catch (error) {
      console.error('Error deleting note:', error);
      setNotes(originalNotes);
      alert('Failed to delete note. Please try again.');
    }
  };

  const handleAiEnhance = async () => {
    if (!task || isAiEnhancing) return;
    
    setIsAiEnhancing(true);
    try {
      const result = await api.aiEnhanceTask(task.id);
      
      // Reload subtasks and notes to show the new ones
      const [subTasksData, notesData] = await Promise.all([
        api.getSubTasks(task.id),
        api.getNotes(task.id)
      ]);
      
      setSubTasks(subTasksData || []);
      setNotes(notesData || []);
      
      // Refresh lists
      const data = await api.getLists();
      setLists(data);
      
      if (onTaskDataChange) {
        onTaskDataChange(task.id);
      }
      
      // Show success notification
      const subtaskText = result.subtasks_created === 1 ? 'sub-task' : 'sub-tasks';
      const noteText = result.notes_created === 1 ? 'note' : 'notes';
      setNotification({
        isVisible: true,
        message: `Added ${result.subtasks_created} ${subtaskText} and ${result.notes_created} ${noteText}`,
        type: 'success'
      });
    } catch (error) {
      console.error('Error enhancing task with AI:', error);
      setNotification({
        isVisible: true,
        message: error.message || 'Failed to enhance task. Please try again.',
        type: 'error'
      });
    } finally {
      setIsAiEnhancing(false);
    }
  };

  const handlePromote = async () => {
    if (!task || isMoving) return;
    
    setIsMoving(true);
    
    try {
      const useTomorrow = isAfterPromotionCutoff(tomorrowCutoffHour);
      await api.promoteToMain(task.id, useTomorrow);
      
      // Refresh lists
      const data = await api.getLists();
      setLists(data);
      
      const targetListName = useTomorrow ? 'To Do Tomorrow' : 'To Do Today';
      setNotification({
        isVisible: true,
        message: `Task promoted to "${targetListName}"`,
        type: 'success'
      });
      
      if (onTaskDataChange) {
        onTaskDataChange(task.id);
      }
    } catch (error) {
      console.error('Error promoting task:', error);
      setNotification({
        isVisible: true,
        message: error.message || 'Failed to promote task. Please try again.',
        type: 'error'
      });
    } finally {
      setIsMoving(false);
    }
  };

  const handleMoveTask = async (targetListId) => {
    if (!task || isMoving) return;
    
    setIsMoving(true);
    setShowMoveModal(false);
    
    try {
      await api.moveTaskToList(task.id, targetListId);
      
      // Refresh lists
      const data = await api.getLists();
      setLists(data);
      
      // Find the task in the new list
      const updatedTask = data
        .flatMap(list => list.tasks)
        .find(t => t.id === task.id);
      
      if (updatedTask) {
        // Find which list it's now in
        const newList = data.find(list => 
          list.tasks.some(t => t.id === task.id)
        );
        
        if (newList) {
          setListId(newList.id);
          setNotification({
            isVisible: true,
            message: `Task moved to "${newList.name}"`,
            type: 'success'
          });
        }
      }
      
      if (onTaskDataChange) {
        onTaskDataChange(task.id);
      }
    } catch (error) {
      console.error('Error moving task:', error);
      setNotification({
        isVisible: true,
        message: error.message || 'Failed to move task. Please try again.',
        type: 'error'
      });
    } finally {
      setIsMoving(false);
    }
  };

  const handleCreateRecurringTask = async (recurringJobData) => {
    if (!task) return;
    
    try {
      // Create the recurring job and update the task in one call
      await api.makeTaskRecurring(task.id, recurringJobData);
      
      // Refresh lists
      const data = await api.getLists();
      setLists(data);
      
      // Update local task
      const updatedTask = data
        .flatMap(list => list.tasks)
        .find(t => t.id === task.id);
      if (updatedTask) {
        setTask(updatedTask);
      }
      
      setShowRecurringModal(false);
      setNotification({
        isVisible: true,
        message: 'Task is now recurring!',
        type: 'success'
      });
      
      if (onTaskDataChange) {
        onTaskDataChange(task.id);
      }
    } catch (error) {
      console.error('Error creating recurring task:', error);
      setNotification({
        isVisible: true,
        message: error.message || 'Failed to create recurring task. Please try again.',
        type: 'error'
      });
    }
  };

  // Handle mention click - navigate to the list
  const handleMentionClick = (mentionedListId) => {
    // Find the list and navigate to it
    const mentionedList = lists.find(l => l.id === mentionedListId);
    if (mentionedList) {
      navigate(`/list/${mentionedListId}`);
    }
  };

  // Find current list to check if we should show promote button
  const currentList = lists?.find(list => list.id === listId);
  const canPromote = task && currentList && !isMainList(currentList.list_type) && !isTomorrowList(currentList.list_type) && !task.completed && !task.promoted;

  if (loading) {
    return (
      <div className="App">
        <MobileHeader />
        <div className="task-detail-loading">
          <div className="spinner"></div>
          <p>Loading task...</p>
        </div>
      </div>
    );
  }

  if (!task) {
    return (
      <div className="App">
        <MobileHeader />
        <div className="task-detail-error">
          <p>Task not found</p>
          <button onClick={handleBack} className="btn-custom btn-primary-custom">
            Go Back
          </button>
        </div>
      </div>
    );
  }

  // Determine the target list for recurring tasks
  // Always use sourceList if it exists (the original list), otherwise use the current list
  const targetListId = task.sourceList ? task.sourceList.id : listId;
  const targetListName = task.sourceList ? task.sourceList.name : (lists?.find(l => l.id === listId)?.name || '');

  // Assignment: eligible assignees are the people the list is shared with (owner + members)
  const isShared = currentList?.is_shared || false;
  const eligibleAssignees = currentList?.shared_users || [];
  const currentAssignee = assignedUserId
    ? (eligibleAssignees.find(u => u.user_id === assignedUserId) ||
       (task.assignedTo ? { user_id: task.assignedTo.userId, user_name: task.assignedTo.name } : null))
    : null;

  const selectAssignee = (selectedUserId) => {
    setIsAssigneeMenuOpen(false);

    // No-op if the selection didn't change
    if ((selectedUserId || null) === (assignedUserId || null)) return;

    const previousUserId = assignedUserId;
    const selectedUser = selectedUserId
      ? eligibleAssignees.find(u => u.user_id === selectedUserId)
      : null;
    const newAssignedTo = selectedUser
      ? { userId: selectedUser.user_id, name: selectedUser.user_name }
      : null;

    // Optimistically update local selection + the shared lists state (which cascades back
    // into this page's `task` via the lookup effect).
    setAssignedUserId(selectedUserId || '');
    if (setLists) {
      setLists(prevLists => prevLists.map(list => ({
        ...list,
        tasks: list.tasks.map(t =>
          t.id === task.id ? { ...t, assignedTo: newAssignedTo } : t
        ),
      })));
    }

    // Fire the API call in the background; revert both optimistic updates on failure.
    api.assignTask(task.id, selectedUserId).catch((error) => {
      console.error('Error assigning task:', error);
      setAssignedUserId(previousUserId);
      if (setLists) {
        setLists(prevLists => prevLists.map(list => ({
          ...list,
          tasks: list.tasks.map(t =>
            t.id === task.id ? { ...t, assignedTo: task.assignedTo || null } : t
          ),
        })));
      }
      setNotification({
        isVisible: true,
        message: error.message || 'Failed to assign task. Please try again.',
        type: 'error'
      });
    });
  };

  // Format an ISO timestamp for the metadata table (e.g. "Jun 19, 2026, 3:04 PM")
  const formatDateTime = (value) => {
    if (!value) return '—';
    const d = new Date(value);
    if (isNaN(d.getTime())) return '—';
    return d.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
  };

  return (
    <div className="App task-detail-page">
      <Notification
        message={notification.message}
        type={notification.type}
        isVisible={notification.isVisible}
        onClose={() => setNotification({ ...notification, isVisible: false })}
      />
      <ListSelectionModal
        isOpen={showMoveModal}
        lists={lists}
        currentListId={listId}
        onSelectList={handleMoveTask}
        onClose={() => setShowMoveModal(false)}
      />
      <RecurringTaskModal
        isOpen={showRecurringModal}
        onClose={() => setShowRecurringModal(false)}
        onSave={handleCreateRecurringTask}
        task={task}
        listId={targetListId}
        listName={targetListName}
      />
      <ConfirmModal
        isOpen={showDeleteModal}
        title="Delete Task"
        message="Are you sure you want to delete this task? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
        isDanger={true}
        onConfirm={async () => {
          setShowDeleteModal(false);
          if (onDeleteTask) {
            await onDeleteTask(listId, task.id);
          }
          navigate(-1);
        }}
        onCancel={() => setShowDeleteModal(false)}
      />
      <MobileHeader />
      <div className="task-detail-container">
        <div className="task-detail-header">
          <button
            className="task-detail-back"
            onClick={handleBack}
            aria-label="Back"
          >
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M12 16l-6-6 6-6M6 10h11" />
            </svg>
            <span>Back</span>
          </button>
          <h1 className="task-detail-title">Task Details</h1>
          <div className="task-detail-header-actions">
            <input
              type="checkbox"
              checked={task.completed}
              onChange={handleToggleTask}
              className="task-detail-checkbox"
              aria-label={`Mark task as ${task.completed ? 'incomplete' : 'complete'}`}
            />
          </div>
        </div>

        <div className="task-detail-content">
          {/* Main Task */}
          <div className="task-detail-section">
            <div className="task-detail-task-wrapper">
              <MentionAutocomplete
                inputRef={taskInputRef}
                value={taskText}
                onChange={handleTaskTextChange}
                onKeyDown={handleTaskTextKeyDown}
                onBlur={handleTaskTextSave}
                lists={lists || []}
                currentListId={listId}
                placeholder="Task name"
                className="task-detail-task-input"
                rows={3}
                disabled={isSavingTask}
              />
              {hasUnsavedChanges && (
                <div className="task-detail-save-hint">
                  <span className="task-detail-save-hint-text">
                    Tap away or use the Save button to save changes
                  </span>
                  <button
                    onClick={handleTaskTextSave}
                    disabled={isSavingTask}
                    className="task-detail-save-btn"
                  >
                    {isSavingTask ? 'Saving...' : 'Save'}
                  </button>
                </div>
              )}
              <div className="task-detail-task-actions">
                <button
                  className="task-detail-ai-button"
                  onClick={handleAiEnhance}
                  disabled={isAiEnhancing}
                  aria-label="Enhance task with AI"
                  title="Use AI to generate helpful notes and sub-tasks"
                >
                {isAiEnhancing ? (
                  <>
                    <span className="task-detail-ai-spinner">⟳</span>
                    <span>Generating...</span>
                  </>
                ) : (
                  <>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M12 0l2.4 7.2L22 9.6l-7.6 2.4L12 19.2l-2.4-7.2L2 9.6l7.6-2.4L12 0z"/>
                    </svg>
                    <span>Help Me Do This</span>
                  </>
                )}
                </button>
                <div className="task-detail-task-actions-icon-group">
                  {canPromote && (
                    <button
                      className="btn-icon btn-promote"
                      onClick={handlePromote}
                      disabled={isMoving}
                      aria-label="Promote task to To Do Today"
                      title="Promote to To Do Today"
                    >
                      ↑
                    </button>
                  )}
                  <button
                    className="btn-icon btn-promote"
                    onClick={() => setShowMoveModal(true)}
                    disabled={isMoving}
                    aria-label="Move task to another list"
                    title="Move to another list"
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <line x1="8" y1="6" x2="21" y2="6"></line>
                      <line x1="8" y1="12" x2="21" y2="12"></line>
                      <line x1="8" y1="18" x2="21" y2="18"></line>
                      <line x1="3" y1="6" x2="3.01" y2="6"></line>
                      <line x1="3" y1="12" x2="3.01" y2="12"></line>
                      <line x1="3" y1="18" x2="3.01" y2="18"></line>
                    </svg>
                  </button>
                  <button
                    className="btn-icon btn-promote"
                    onClick={() => setShowRecurringModal(true)}
                    aria-label="Make task recurring"
                    title="Make task recurring"
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                      <line x1="16" y1="2" x2="16" y2="6"></line>
                      <line x1="8" y1="2" x2="8" y2="6"></line>
                      <line x1="3" y1="10" x2="21" y2="10"></line>
                    </svg>
                  </button>
                  <button
                    className="btn-icon btn-delete"
                    onClick={() => setShowDeleteModal(true)}
                    aria-label="Delete task"
                    title="Delete task"
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M3 6h18M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2m3 0v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6h14zM10 11v6M14 11v6" />
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Assignee (shared lists only) */}
          {isShared && (
            <div className="task-detail-section">
              <label className="task-detail-label">Assigned to</label>
              <div className="assignee-dropdown assignee-dropdown--block" ref={assigneeRef}>
                <button
                  type="button"
                  className="assignee-dropdown-trigger"
                  onClick={() => setIsAssigneeMenuOpen(open => !open)}
                  aria-haspopup="listbox"
                  aria-expanded={isAssigneeMenuOpen}
                >
                  <svg className="assignee-dropdown-person-icon" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
                    <circle cx="9" cy="7" r="4" />
                    <line x1="19" y1="8" x2="19" y2="14" />
                    <line x1="22" y1="11" x2="16" y2="11" />
                  </svg>
                  {currentAssignee ? (
                    <>
                      <UserAvatar user={{ userId: currentAssignee.user_id, name: currentAssignee.user_name }} size="small" />
                      <span className="assignee-dropdown-trigger-name">{currentAssignee.user_name}</span>
                    </>
                  ) : (
                    <span className="assignee-dropdown-trigger-placeholder">Assign to someone</span>
                  )}
                  <svg className="assignee-dropdown-caret" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="6 9 12 15 18 9" />
                  </svg>
                </button>
                {isAssigneeMenuOpen && (
                  <div className="assignee-dropdown-menu" role="listbox" aria-label="Assign this task to">
                    <div className="assignee-dropdown-header">Assign to</div>
                    <button
                      type="button"
                      role="option"
                      aria-selected={!assignedUserId}
                      className={`assignee-dropdown-item ${!assignedUserId ? 'assignee-dropdown-item-selected' : ''}`}
                      onClick={() => selectAssignee(null)}
                    >
                      <span className="assignee-dropdown-item-none">∅</span>
                      <span className="assignee-dropdown-item-name">Unassigned</span>
                    </button>
                    {eligibleAssignees.map(u => (
                      <button
                        type="button"
                        key={u.user_id}
                        role="option"
                        aria-selected={assignedUserId === u.user_id}
                        className={`assignee-dropdown-item ${assignedUserId === u.user_id ? 'assignee-dropdown-item-selected' : ''}`}
                        onClick={() => selectAssignee(u.user_id)}
                      >
                        <UserAvatar user={{ userId: u.user_id, name: u.user_name }} size="small" />
                        <span className="assignee-dropdown-item-name">{u.user_name}</span>
                        {assignedUserId === u.user_id && <span className="assignee-dropdown-check">✓</span>}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Sub-tasks */}
          <div className="task-detail-section">
            <label className="task-detail-label">Sub-tasks</label>
            <div className="task-detail-subtasks">
              {subTasks.length === 0 ? (
                <p className="task-detail-empty">No sub-tasks yet. Add one below.</p>
              ) : (
                <ul className="task-detail-subtask-list">
                  {subTasks.map(subTask => (
                    <li key={subTask.id} className="task-detail-subtask-item">
                      <label className="task-detail-subtask-checkbox-label">
                        <input
                          type="checkbox"
                          checked={subTask.completed}
                          onChange={() => handleToggleSubTask(subTask.id)}
                          className="task-detail-subtask-checkbox"
                        />
                        <span className={subTask.completed ? 'task-detail-subtask-text-completed' : ''}>
                          {linkifyText(subTask.text, lists, handleMentionClick)}
                        </span>
                      </label>
                      <button
                        className="task-detail-subtask-delete"
                        onClick={() => handleDeleteSubTask(subTask.id)}
                        aria-label={`Delete sub-task "${subTask.text}"`}
                      >
                        ×
                      </button>
                    </li>
                  ))}
                </ul>
              )}
              <div className="task-detail-subtask-input-group">
                <MentionAutocomplete
                  inputRef={subTaskInputRef}
                  value={newSubTaskText}
                  onChange={(e) => setNewSubTaskText(e.target.value)}
                  onKeyDown={handleSubTaskKeyDown}
                  lists={lists || []}
                  currentListId={listId}
                  className="task-detail-subtask-input"
                  placeholder="Add a sub-task..."
                />
                <button
                  onClick={handleAddSubTask}
                  disabled={!newSubTaskText.trim()}
                  className="task-detail-subtask-add-btn"
                >
                  Add
                </button>
              </div>
            </div>
          </div>

          {/* Notes */}
          <div className="task-detail-section">
            <label className="task-detail-label">Notes</label>
            <div className="task-detail-notes">
              {notes.length === 0 ? (
                <p className="task-detail-empty">No notes yet. Add one below.</p>
              ) : (
                <ul className="task-detail-note-list">
                  {notes.map(note => (
                    <li key={note.id} className="task-detail-note-item">
                      <p className="task-detail-note-text">{linkifyText(note.text, lists, handleMentionClick)}</p>
                      <button
                        className="task-detail-note-delete"
                        onClick={() => handleDeleteNote(note.id)}
                        aria-label={`Delete note`}
                      >
                        ×
                      </button>
                    </li>
                  ))}
                </ul>
              )}
              <div className="task-detail-note-input-group">
                <MentionAutocomplete
                  inputRef={noteInputRef}
                  value={newNoteText}
                  onChange={(e) => setNewNoteText(e.target.value)}
                  onKeyDown={handleNoteKeyDown}
                  lists={lists || []}
                  currentListId={listId}
                  className="task-detail-note-input"
                  placeholder="Add a note... (Press Enter to add, Shift+Enter for new line)"
                  rows={2}
                />
                <button
                  onClick={handleAddNote}
                  disabled={!newNoteText.trim()}
                  className="task-detail-note-add-btn"
                >
                  Add
                </button>
              </div>
            </div>
          </div>

          {/* Task metadata table (shared lists only) */}
          {isShared && (
            <table className="task-drawer-meta-table">
              <tbody>
                {task.createdBy && task.createdBy.name && (
                  <tr>
                    <th>Created by</th>
                    <td>
                      <span className="task-drawer-meta-creator">
                        <UserAvatar user={task.createdBy} size="small" />
                        {task.createdBy.name}
                      </span>
                    </td>
                  </tr>
                )}
                <tr>
                  <th>Created</th>
                  <td>{formatDateTime(task.createdAt)}</td>
                </tr>
                <tr>
                  <th>Last updated</th>
                  <td>{formatDateTime(task.updatedAt)}</td>
                </tr>
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}

